import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const SubscriptionPrompt = ({ onClose }) => {
  const { createCheckoutSession } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const checkoutUrl = await createCheckoutSession(selectedPlan);
      window.location.href = checkoutUrl;
    } catch (error) {
      alert('Failed to create checkout session. Please try again.');
      setLoading(false);
    }
  };

  const getTitle = () => {
    return "Upgrade to Pro";
  };

  const getDescription = () => {
    return "You need an active subscription to generate videos. Upgrade to Pro for unlimited access.";
  };

  const plans = {
    monthly: {
      price: '$4.99',
      period: 'per month',
      savings: null
    },
    yearly: {
      price: '$49',
      period: 'per year',
      savings: 'Save 18%'
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '480px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          position: 'relative'
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            color: '#6b7280',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
          onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          ×
        </button>

        <div style={{ textAlign: 'center' }}>
          {/* Icon */}
          <div 
            style={{
              width: '64px',
              height: '64px',
              backgroundColor: '#dbeafe',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto'
            }}
          >
            <svg 
              style={{ width: '32px', height: '32px', color: '#2563eb' }}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" 
              />
            </svg>
          </div>
          
          {/* Title */}
          <h3 
            style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '12px',
              lineHeight: '1.3'
            }}
          >
            {getTitle()}
          </h3>
          
          {/* Description */}
          <p 
            style={{
              color: '#6b7280',
              marginBottom: '32px',
              fontSize: '16px',
              lineHeight: '1.5'
            }}
          >
            {getDescription()}
          </p>
          
          {/* Plan Selection */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
              {Object.entries(plans).map(([planKey, plan]) => (
                <button
                  key={planKey}
                  onClick={() => setSelectedPlan(planKey)}
                  style={{
                    flex: 1,
                    padding: '16px',
                    border: selectedPlan === planKey ? '2px solid #2563eb' : '2px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: selectedPlan === planKey ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s'
                  }}
                >
                  {plan.savings && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '8px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontWeight: '500'
                      }}
                    >
                      {plan.savings}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#111827',
                      marginBottom: '4px'
                    }}
                  >
                    {plan.price}
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#6b7280'
                    }}
                  >
                    {plan.period}
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Features */}
          <div 
            style={{
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '32px',
              border: '1px solid #e5e7eb',
              textAlign: 'left'
            }}
          >
            {[
              'Unlimited video generations',
              'AI script generation',
              'High-quality TTS voices',
              'No watermarks'
            ].map((feature, index) => (
              <div 
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: index === 3 ? '0' : '12px',
                  fontSize: '14px',
                  color: '#374151'
                }}
              >
                <svg 
                  style={{
                    width: '16px',
                    height: '16px',
                    color: '#10b981',
                    marginRight: '12px',
                    flexShrink: 0
                  }}
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                    clipRule="evenodd" 
                  />
                </svg>
                {feature}
              </div>
            ))}
          </div>
          
          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px 24px',
                color: '#374151',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#e5e7eb'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            >
              Cancel
            </button>
            <button
              onClick={handleUpgrade}
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px 24px',
                backgroundColor: loading ? '#9ca3af' : '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => {
                if (!loading) e.target.style.backgroundColor = '#1d4ed8';
              }}
              onMouseOut={(e) => {
                if (!loading) e.target.style.backgroundColor = '#2563eb';
              }}
            >
              {loading ? 'Loading...' : 'Subscribe Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPrompt;
